import React, { useContext, useEffect, useState, useRef } from "react";
import { Spinner, Button } from "reactstrap";
import { injectIntl } from "react-intl";

import { useFetch } from "../Base/Hooks/fetch";
import { useDelete } from "../Base/Hooks/delete";
import { useToken } from "../Base/Hooks/token";
import { EnvironmentContext, environments } from "../../config/EnvironmentContext";

import PageDisplay from "../Base/PageDisplay/PageDisplay";
import FormBuilder from "../Base/Forms/FormBuilder";
import Modal from "../Base/Modal/Modal";
import formSetup from "./formSetup";

const VideoProviderSettings = (props) => {
  const { title, history, match, sport, entityType } = props;
  const { organizationId, competitionId, seasonId, fixtureId } = match.params;

  const [refreshFlag, setRefreshFlag] = useState(true);
  const [refreshDeleteData, setRefreshDeleteData] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [currentSetting, setCurrentSetting] = useState(null);
  const [formData, setFormData] = useState({
    sport,
    organizationId,
    competitionId,
    seasonId,
    fixtureId,
    defaultPlatformProvider: "LOCAL",
  });

  const pageRef = useRef();
  const { token } = useToken();
  const env = useContext(EnvironmentContext);

  let fetchUrl;
  let deleteUrl;
  let formKey;
  let pageTitle;

  if (entityType === "fixture") {
    fetchUrl = `/v1/${sport}/o/${organizationId}/video/provider/settings?fixtureId=${fixtureId}`;
    deleteUrl = `/v1/${sport}/o/${organizationId}/fixtures/${fixtureId}/video/provider/settings`;
    formKey = "SetFixtureVideoProviderSettings";
    pageTitle = "Fixture Video Provider Settings";
  } else if (entityType === "season") {
    fetchUrl = `/v1/${sport}/o/${organizationId}/competitions/${competitionId}/video/provider/settings?seasonId=${seasonId}`;
    deleteUrl = `/v1/${sport}/o/${organizationId}/seasons/${seasonId}/video/provider/settings`;
    formKey = "SetSeasonVideoProviderSettings";
    pageTitle = "Season Video Provider Settings";
  } else {
    fetchUrl = `/v1/${sport}/o/${organizationId}/competitions/${competitionId}/video/provider/settings`;
    deleteUrl = `/v1/${sport}/o/${organizationId}/competitions/${competitionId}/video/provider/settings`;
    formKey = "SetCompetitionVideoProviderSettings";
    pageTitle = "Competition Video Provider Settings";
  }

  const { error, loading, data } = useFetch(fetchUrl, "", refreshFlag);
  const { deleteError, deleteLoading, deleteData } = useDelete(deleteUrl, "", refreshDeleteData);

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO
  useEffect(() => {
    if (data) {
      let setting = null;
      if (entityType === "fixture") {
        setting = data.data?.[0] || null;
      } else if (entityType === "season") {
        setting = data.data?.find((item) => !item.fixtureId) || null;
      } else {
        setting = data.data?.find((item) => !item.seasonId && !item.fixtureId) || null;
      }
      setCurrentSetting(setting);
      setFormData({
        sport,
        organizationId,
        competitionId,
        seasonId,
        fixtureId,
        defaultPlatformProvider: setting?.defaultPlatformProvider || "LOCAL",
      });
      setRefreshFlag(false);
    }
  }, [data]);

  useEffect(() => {
    if (deleteData) {
      pageRef.current.addFlash("Video provider settings deleted successfully.", "success", 5);
      setCurrentSetting(null);
      setFormData((prev) => ({ ...prev, defaultPlatformProvider: "LOCAL" }));
      setRefreshFlag(true);
    }
    if (deleteError) {
      pageRef.current.addFlash("Delete failed: " + deleteError, "danger", 10);
    }
    setRefreshDeleteData(false);
  }, [deleteData, deleteError]);

  async function handleFormSubmit(values) {
    const authHeader = { Authorization: token.tokenType + " " + token.token };
    const baseUrl = environments.location[env];
    const headers = {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...authHeader,
    };

    try {
      if (currentSetting) {
        const deleteResponse = await fetch(baseUrl + deleteUrl, { method: "DELETE", headers, mode: "cors" });
        if (!deleteResponse.ok) {
          const body = await deleteResponse.json();
          pageRef.current.addFlash(body.message || "Delete failed", "danger", 10);
          return;
        }
      }

      const postResponse = await fetch(`${baseUrl}/v1/${sport}/o/${organizationId}/video/provider/settings`, {
        method: "POST",
        headers,
        mode: "cors",
        body: JSON.stringify(values),
      });

      if (postResponse.ok) {
        pageRef.current.addFlash("Default Platform Provider updated successfully.", "success", 5);
        setRefreshFlag(true);
      } else {
        const body = await postResponse.json();
        pageRef.current.addFlash(body.message || "Save failed", "danger", 10);
      }
    } catch (e) {
      pageRef.current.addFlash(String(e), "danger", 10);
    }
  }

  return (
    <React.Fragment>
      {loading && <Spinner size="md" color="orange" />}
      {!loading && (
        <PageDisplay
          title={pageTitle}
          error={error}
          pageTitle={pageTitle + " - " + title}
          ref={pageRef}
          history={history}
        >
          <div className="mb-3">
            {currentSetting ? (
              <React.Fragment>
                <p>
                  Current setting: <strong>{currentSetting.defaultPlatformProvider}</strong>
                </p>
                <Button
                  outline
                  color="danger"
                  size="sm"
                  disabled={deleteLoading}
                  onClick={() => setDeleteModalOpen(true)}
                >
                  {deleteLoading ? <Spinner size="sm" /> : <i className="fas fa-minus" />} Delete Setting
                </Button>
                <Modal
                  isOpen={deleteModalOpen}
                  heading="Delete Video Provider Settings?"
                  text="This will remove the default platform provider setting. Are you sure?"
                  actionText="Delete"
                  cancelText="Cancel"
                  action={() => {
                    setRefreshDeleteData(true);
                    setDeleteModalOpen(false);
                  }}
                  cancel={() => setDeleteModalOpen(false)}
                />
              </React.Fragment>
            ) : (
              <p className="text-muted">No default platform provider configured.</p>
            )}
          </div>
          <hr />
          <FormBuilder
            form={formKey}
            action={handleFormSubmit}
            formSetup={formSetup}
            pageRef={pageRef}
            formData={formData}
          />
        </PageDisplay>
      )}
    </React.Fragment>
  );
};

export default injectIntl(VideoProviderSettings);
